"use client";

import { useState } from "react";
import { useRouter } from "@bprogress/next";

import { SearchTrigger } from "@/components/common/search-trigger";
import { trans } from "@/service/i18n/i18n";
import { SearchModal } from "@/components/common/search-modal/search-modal";
import {
  CalendarDaysIcon,
  CheckIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { createPath } from "@/service/routing/routing";
import { ExamTypeEnum } from "@/types/exams-enums";
import { examTypeIdToName } from "@/service/exams/exam-type-map";
import { cn } from "@/lib/utils";
import { SectionTitleWithIcon } from "@/components/common/section-title-with-icon";

const EXAM_TYPE_NAMES = ["PES", "LEK", "LDEK"] as const;

export const ExamsTitleWithActions = ({
  searchContext,
  type,
  specialization,
  specializations,
  hideType = false,
  degradeHeader = false,
}: {
  searchContext: ExamSearchContext;
  type?: 1 | 2 | 3;
  specialization?: string;
  specializations?: { id: string; name: string }[];
  hideType?: boolean;
  degradeHeader?: boolean;
}) => {
  const showMenu = !hideType && !!type;
  const HeadingTag = degradeHeader ? "p" : "h1";
  const heading = showMenu ? `Egzamin ${EXAM_TYPE_NAMES[type - 1]}` : null;

  return (
    <div
      className={
        "mb-4 flex w-full flex-wrap items-center justify-between gap-4 sm:mb-8"
      }
    >
      <div
        className={`text-foreground flex w-full flex-row items-center justify-between gap-3 sm:w-auto sm:justify-start`}
      >
        <SectionTitleWithIcon sectionKey={"exam"}>
          <HeadingTag className={"text-xl font-semibold md:text-2xl"}>
            {heading ? (
              heading
            ) : (
              <>
                <span className="sm:hidden">Egzaminy</span>
                <span className="hidden sm:inline">{trans("exams.title")}</span>
              </>
            )}
          </HeadingTag>
        </SectionTitleWithIcon>
        {showMenu && (
          <ExamSetupMenu
            type={type}
            specialization={specialization}
            specializations={specializations}
          />
        )}
      </div>

      <div className={"flex items-center gap-2"}>
        <div className={"hidden sm:block"}>
          <SearchModal
            preventShortcuts={true}
            examSearchContext={searchContext}
          >
            <SearchTrigger placeholder={trans(`global_search.exam.title`)} />
          </SearchModal>
        </div>
      </div>
    </div>
  );
};

const ExamSetupMenu = ({
  type,
  specialization,
  specializations,
}: {
  type: 1 | 2 | 3;
  specialization?: string;
  specializations?: { id: string; name: string }[];
}) => {
  const [isOpen, setOpen] = useState(false);
  const router = useRouter();

  const currentTypeName = EXAM_TYPE_NAMES[type - 1];
  const hasSpecializations =
    type === 1 && !!specializations && specializations.length > 0;

  const changeType = (nextType: (typeof EXAM_TYPE_NAMES)[number]) => {
    setOpen(false);
    if (nextType === currentTypeName) return;

    if (nextType === "PES") {
      window.location.href = createPath("exam.pes_choose_specialization");
      return;
    }

    window.location.href = createPath("exam.index", {
      examType: nextType === "LEK" ? 2 : 3,
    });
  };

  const chooseSpecialization = () => {
    setOpen(false);
    router.push(createPath("exam.pes_choose_specialization"));
  };

  return (
    <Popover open={isOpen} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="size-9 shrink-0"
          aria-label="Opcje egzaminu"
        >
          <MoreHorizontalIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-1000 w-[min(280px,calc(100vw-2rem))] p-0"
      >
        <Command>
          <CommandList className="max-h-[min(360px,60vh)]">
            <CommandEmpty>{trans("empty")}</CommandEmpty>

            <CommandGroup heading="Rodzaj egzaminu">
              {EXAM_TYPE_NAMES.map((name) => {
                const isPesWithSpec = name === "PES" && hasSpecializations;

                return (
                  <CommandItem
                    key={name}
                    value={`typ ${name}`}
                    onSelect={() => changeType(name)}
                  >
                    <CheckIcon
                      className={cn(
                        name === currentTypeName ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {name}
                    {/* dla PES pokaż wybraną specjalizację inline + akcję "Zmień",
                        która otwiera okno wyboru zamiast listy w menu */}
                    {isPesWithSpec && specialization && (
                      <span className="text-muted-foreground ml-1.5 min-w-0 flex-1 truncate text-xs">
                        {specialization}
                      </span>
                    )}
                    {isPesWithSpec && (
                      <Button
                        variant="inline"
                        size="inline"
                        className="ml-auto shrink-0 gap-0.5 text-xs font-medium"
                        onClick={(e) => {
                          e.stopPropagation();
                          chooseSpecialization();
                        }}
                      >
                        Zmień
                        <ChevronRightIcon className="size-3.5" />
                      </Button>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>

            <CommandSeparator className="my-1" />

            <CommandGroup>
              <CommandItem
                value="terminarz"
                onSelect={() => {
                  setOpen(false);
                  router.push(
                    createPath("exam.schedule", {
                      examType: examTypeIdToName(type as ExamTypeEnum) ?? null,
                    }),
                  );
                }}
              >
                <CalendarDaysIcon />
                Terminarz
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
